import { computed, Directive, EventEmitter, inject, Injector, Input, Output, Signal, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import {
   LcsAdvancedMultiSelectDatasource,
   LcsAdvancedMultiSelectOpenDialogEvent,
   LcsAdvancedMultiSelectReadonlyStateType,
   LcsAdvancedMultiSelectStateEventPayloads,
   LcsAdvancedMultiSelectTriggerConfig,
   LcsAdvancedMultiSelectTriggerFormGroup,
} from '@lcs/shared/advanced-multi-select';
import { CallState, LoadingState } from '@lcs/shared/api';
import { LcsSelectWithAllDisplayTypes } from '@lcs/shared/select-with-all';
import { Selector, Store } from '@ngrx/store';
import { map } from 'rxjs/operators';
import {
   LoadActionCreator,
   SearchActionCreator,
   SearchResetActionCreator,
} from '../entity-select/select/eager-select/eager-select-actions';

@Directive()
export abstract class AdvancedMultiSelectBase<TItem extends { name: string | null }> {
   @Input() set parentDirty(value: boolean) {
      if (value) {
         this.triggerConfig().group?.controls.items.markAsTouched();
         this.triggerConfig().group?.controls.items.updateValueAndValidity();
      }
   }

   @Input()
   get itemsControl(): FormControl<TItem[]> | null {
      return this._itemsControl();
   }
   set itemsControl(value: FormControl<TItem[]> | null) {
      this._itemsControl.set(value);
      this._updateItemsFromControl();
   }
   private _itemsControl = signal<FormControl<TItem[]> | null>(null);

   @Input()
   get allControl(): FormControl<boolean> | null {
      return this._allControl();
   }
   set allControl(value: FormControl<boolean> | null) {
      this._allControl.set(value);
      this._updateAllFromControl();
   }
   private _allControl = signal<FormControl<boolean> | null>(null);

   @Output() change = new EventEmitter<void>();

   readonly store = inject(Store);
   readonly injector = inject(Injector);

   readonly items: Signal<TItem[]> = this.store.selectSignal(this.options.selectFilteredItems);
   readonly callState: Signal<CallState> | null = this.options.selectCallState
      ? this.store.selectSignal(this.options.selectCallState)
      : null;

   // Move toSignal outside of computed, per Angular 17+ signal requirements
   // https://v17.angular.io/errors/NG0602
   private _itemsSignal = signal<Signal<TItem[]> | null>(null);
   private _allSignal = signal<Signal<boolean> | null>(null);

   readonly itemsFromControl = computed(
      () => {
         const sig = this._itemsSignal();
         return sig ? sig() : [];
      },
      { equal: (a, b) => JSON.stringify(a) === JSON.stringify(b) }
   );

   readonly allFromControl = computed(() => {
      const sig = this._allSignal();
      return sig ? sig() : false;
   });

   private _updateItemsFromControl() {
      const itemsControl = this._itemsControl();
      if (itemsControl) {
         const sig = toSignal(itemsControl.valueChanges.pipe(map(() => itemsControl.getRawValue() ?? [])), {
            initialValue: itemsControl.getRawValue() ?? [],
            injector: this.injector,
         });
         this._itemsSignal.set(sig);
      } else {
         this._itemsSignal.set(null);
      }
   }

   private _updateAllFromControl() {
      const allControl = this._allControl();
      if (allControl) {
         const sig = toSignal(allControl.valueChanges.pipe(map(() => allControl.getRawValue() ?? false)), {
            initialValue: allControl.getRawValue() ?? false,
            injector: this.injector,
         });
         this._allSignal.set(sig);
      } else {
         this._allSignal.set(null);
      }
   }

   readonly selectedItems = computed(() => {
      const items = this.itemsFromControl();
      return signal(items);
   });

   readonly datasource = computed<LcsAdvancedMultiSelectDatasource<TItem>>(() => {
      return {
         id: 1,
         label: this.labelSignal(),
         items: this.items() ?? [],
         selectedItems: this.selectedItems()(),
         itemTrackBy: this.itemTrackBy,
         displayOptions: {
            displayWithFn: (item: TItem) => item.name ?? '',
         },
         callState: this.callState ? this.callState() : LoadingState.Init,
      };
   });

   readonly triggerConfig = computed<LcsAdvancedMultiSelectTriggerConfig<TItem>>(() => ({
      displayOptions: {
         label: this.labelSignal(),
         displayType: LcsSelectWithAllDisplayTypes.Chips,
         displayWithFn: (item: TItem, state: LcsAdvancedMultiSelectReadonlyStateType<TItem>) => {
            return item.name ?? '';
         },
         hasAllCheckbox: !!this.options.hasAllCheckbox,
      },
      group: new LcsAdvancedMultiSelectTriggerFormGroup({
         items: new FormControl(this.itemsFromControl(), { nonNullable: true }),
         all: new FormControl(this.allFromControl(), { nonNullable: true }),
      }),
      syncOn: 'save',
   }));

   abstract readonly labelSignal: Signal<string>;

   constructor(
      public options: {
         loadAction?: LoadActionCreator;
         additionalLoadFields?: string[];
         loadFilters?: string[];
         searchResetAction: SearchResetActionCreator;
         searchAction: SearchActionCreator;
         selectFilteredItems: Selector<any, TItem[]>;
         selectCallState?: Selector<any, CallState>;
         hasAllCheckbox?: boolean;
      }
   ) {}

   onOpen(e: LcsAdvancedMultiSelectOpenDialogEvent<TItem>) {
      this.selectedItems().set(this.itemsFromControl());
      const { loadAction, additionalLoadFields, loadFilters } = this.options;
      if (loadAction) {
         this.store.dispatch(
            loadAction({
               additionalFields: additionalLoadFields ?? [],
               filterOverrides: loadFilters,
            })
         );
      }
      this.store.dispatch(this.options.searchResetAction());
   }

   onSave(e: LcsAdvancedMultiSelectStateEventPayloads<TItem>['save']) {
      this.store.dispatch(this.options.searchResetAction());
      const items = e.state.selectedItems();
      this.itemsControl?.setValue(items);
      this.triggerConfig().group?.controls.items.markAsTouched();
      this.triggerConfig().group?.controls.items.updateValueAndValidity();
      this.change.emit();
   }

   onSelectedItemsChange(e: LcsAdvancedMultiSelectStateEventPayloads<TItem>['selectedItemsChange']) {
      this.selectedItems().set(e.state.selectedItems());
   }

   onClose(e: LcsAdvancedMultiSelectStateEventPayloads<TItem>['close']) {
      this.store.dispatch(this.options.searchResetAction());

      const items = this.itemsFromControl();
      this.selectedItems().set(items);
      e.actions.setSelectedItems(items);
      e.actions.syncTriggerGroup();

      const triggerItemsControl = e.state.triggerFormGroup().controls.items;
      if (!this.itemsControl?.dirty) {
         triggerItemsControl.markAsPristine();
      }
      if (!this.itemsControl?.touched) {
         triggerItemsControl.markAsUntouched();
      }
   }

   onSearch(e: LcsAdvancedMultiSelectStateEventPayloads<TItem>['search']) {
      this.store.dispatch(this.options.searchAction({ searchTerm: e.searchText }));
   }

   abstract itemTrackBy: (a: TItem) => any;
}
